import { Id, Identifiable, OrganizationalUnit, Resource } from "@planzone/dto";
import { OrganizationalUnitsRepository } from "../../adapters/repository";
import { present } from "../../../utils/presenter";
import { Interactor } from "./types";

// Only the resources the back actually moved are presented: the others were already where asked.
function keepChanged(resources: Identifiable<Resource>[], changedUuids: Id[]) {
  return resources.filter(({ uuid }) => changedUuids.includes(uuid));
}

function createInteractor(
  repository: OrganizationalUnitsRepository,
): Interactor {
  return ({
    organizationalUnit,
    name,
    assignedResources,
    unassignedResources,
  }) =>
    present(
      (name !== organizationalUnit.name
        ? repository.editOrganizationalUnit(organizationalUnit, name)
        : Promise.resolve(organizationalUnit)
      )
        .then((editedOrganizationalUnit) =>
          Promise.all([
            assignedResources.length > 0
              ? repository.assignResourcesToOrganizationalUnit(
                  organizationalUnit,
                  assignedResources.map(({ uuid }) => uuid),
                )
              : Promise.resolve([]),
            unassignedResources.length > 0
              ? repository.unassignResourcesFromOrganizationalUnit(
                  organizationalUnit,
                  unassignedResources.map(({ uuid }) => uuid),
                )
              : Promise.resolve([]),
          ]).then(([assignedUuids, unassignedUuids]) => {
            const unit: Identifiable<OrganizationalUnit> = {
              uuid: editedOrganizationalUnit.uuid,
              name: editedOrganizationalUnit.name,
              rankKey: editedOrganizationalUnit.rankKey,
              parentUuid: editedOrganizationalUnit.parentUuid,
            };

            return {
              organizationalUnit: editedOrganizationalUnit,
              assignedResources: keepChanged(
                assignedResources,
                assignedUuids,
              ).map((resource) => ({ ...resource, organizationalUnit: unit })),
              unassignedResources: keepChanged(
                unassignedResources,
                unassignedUuids,
              ).map((resource) => ({
                ...resource,
                organizationalUnit: null,
              })),
            };
          }),
        )
        .catch(() => Promise.reject({ organizationalUnit })),
    );
}

export type { Interactor };

export { createInteractor };
